const HUBSPOT_REGION = 'na1'

// The developer embed's form definition ships two inline <style> injectors
// (HubSpot's BaseStyle + per-form theme). We keep those blocked by CSP so the
// site's own form styling keeps winning the `.hsfc-*` specificity contest
// (P180PCM-367) — the nonce bridge below skips them by matching their content.
const HUBSPOT_STYLE_INJECTOR = /hsfc-id="BaseStyle"|hsf-style-namespace/

// True for the embed payload's two style-injector scripts (which we leave
// blocked); false for the form's hydration scripts (which we nonce so they run).
function isHubSpotFormStyleInjector(scriptText: string): boolean {
  return HUBSPOT_STYLE_INJECTOR.test(scriptText)
}

/**
 * A script the nonce bridge should stamp: one the embed is inserting into a
 * `.hs-form-html` subtree that hydrates the form (i.e. not a style injector).
 */
export function shouldNonceEmbedScript(node: Node, parent: Node): boolean {
  return (
    node instanceof HTMLScriptElement
    && parent instanceof Element
    && parent.closest('.hs-form-html') !== null
    && !isHubSpotFormStyleInjector(node.textContent ?? '')
  )
}

let nonceBridgeInstalled = false

/**
 * The CSP nonce for this document. nuxt-security's `useNonce()` only resolves
 * during SSR, so on the client we read the nonce off an element it already
 * stamped — it is fixed for the document's lifetime and is not a secret.
 */
function resolveNonce(): string | undefined {
  for (const script of Array.from(document.scripts)) {
    if (script.nonce)
      return script.nonce
  }
  return undefined
}

/**
 * Our CSP is `script-src 'nonce-…' …`, so inline scripts without the nonce are
 * refused. The developer embed renders each form by rebuilding every <script>
 * from the fetched definition (`createElement` + `replaceChild`, copying only
 * HubSpot's own attributes), so the rebuilt hydration scripts carry no nonce
 * and are blocked — the form renders as static markup but never becomes
 * interactive (P180PCM-395).
 *
 * Stamp our nonce onto each rebuilt script as it is inserted into a
 * `.hs-form-html` subtree so it is allowed to run, skipping the style injectors
 * on purpose. The embed rebuilds scripts on load and, for late-mounted forms,
 * from its own MutationObserver, so the patch stays for the document's lifetime.
 */
function installNonceBridge(): void {
  if (nonceBridgeInstalled)
    return

  const nonce = resolveNonce()
  if (!nonce)
    return

  nonceBridgeInstalled = true

  const originalReplaceChild = Node.prototype.replaceChild
  Node.prototype.replaceChild = function replaceChildWithNonce<T extends Node>(this: Node, node: Node, child: T): T {
    if (shouldNonceEmbedScript(node, this))
      (node as HTMLScriptElement).setAttribute('nonce', nonce)

    return originalReplaceChild.call(this, node, child) as T
  }
}

/** Loads HubSpot's developer (non-iframe) per-portal embed script; re-appending on each mount re-scans for late-mounted forms. */
export function useHubSpotLoader(portalId: string) {
  installNonceBridge()

  const src = `https://js.hsforms.net/forms/embed/developer/${portalId}.js`
  return new Promise<void>((resolve, reject) => {
    document.head.querySelectorAll(`script[src="${src}"]`).forEach(el => el.remove())
    const script = document.createElement('script')
    script.src = src
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`HubSpot forms embed failed to load for portal ${portalId}`))
    document.head.appendChild(script)
  })
}

/**
 * Render a HubSpot form into an existing container (the footer/sidebar/error
 * mount points that live in the layout, page and error templates). Ids are
 * resolved at mount so the app-settings store is populated.
 */
export function useHubSpotFormHtml(
  containerId: string,
  resolve: () => { portalId?: string, formId?: string },
) {
  onMounted(async () => {
    const { portalId, formId } = resolve()
    if (!portalId || !formId) {
      return
    }

    const container = document.getElementById(containerId)
    if (container) {
      const form = document.createElement('div')
      form.className = 'hs-form-html'
      form.dataset.region = HUBSPOT_REGION
      form.dataset.portalId = portalId
      form.dataset.formId = formId
      container.replaceChildren(form)
    }

    await useHubSpotLoader(portalId)
  })
}
